import React, { Component, useEffect } from "react";
import { reaction } from "mobx";
import { inject, observer } from "mobx-react";
import { AppStore, StrategicPlanStore} from "../../stores";
import { Ticket, TicketStore} from "Stores/dashboardStore/tickets";
import * as ActionplanData from "./d3support/ActionplanData";
import { categories, viewBys, DateSlider } from "./d3support/Controls";
import { D3NumberNav} from "./d3support/D3NumberNav";
import { ActionplanGraphLabels, ActionplanGraph } from "./d3support/ActionplanGraph";
import { ActionplanFilters } from "./d3support/Filters";
import { ActionplanTimeline } from "./d3support/Timeline";
import {
    ActionplanState,
    EntityCounts,
    setActionplans
} from "./d3support/ActionplanData";
import { useSearchParams } from "react-router-dom";

// D3DB stands for D3Dashboard
export interface D3DBProps {
    appStore?: AppStore
    ticketStore?: TicketStore
    strategicPlanStore?: StrategicPlanStore
    isStrategic?: boolean
    overdue?: boolean
}

interface D3DBState {
    isStrategic?: boolean
    actionplanData: ActionplanData.ActionplanState
    statuses: {[key: string]: boolean}
    view: string
}


@inject("appStore", "ticketStore", "strategicPlanStore")
@observer
export class D3Dashboard extends Component<D3DBProps, D3DBState> {
    _isMounted = false;

    constructor(props: D3DBProps) {
        super(props);

        let actionplanData: ActionplanState = {
            viewBy: viewBys.ASSIGNEE,
            minDate: undefined,
            maxDate: undefined,
            dateRange: undefined,
            minFilteredDate: undefined,
            maxFilteredDate: undefined,
            allActionplans: [],
            filteredActionplans: [],
            counts: ActionplanData.newCountMap(),
            viewByCounts: new Map<string, EntityCounts>(),
            sortedViewByCounts: [],
            viewByFilters: undefined,
            labelGroupFilters: undefined,
            labelFilters: undefined,
            keywords: undefined,
            focusedViewBy: undefined,
            focusedCategory: undefined,
            sortCategory: undefined,
            sortDirection: 0,
            maxCount: 0,
            includeAllStatus: true,
            includeOpenLate: props.overdue || false,
            includeOpenOnTime: false,
            includeClosedMet: false,
            includeClosedNotMet: false,
            includeNoDate: false
        };
        actionplanData = setActionplans(
            actionplanData,
            props.ticketStore?.allTickets || []
        );
        const statuses = {
            grand_total: true,
            "open-late": props.overdue || false,
            "closed-not-met": false,
            "open-on-time": false,
            "closed-met": false,
            "no-date": false
        };
        this.state = {
            isStrategic: this.props.isStrategic,
            view: "Status",
            statuses,
            actionplanData: ActionplanData.setStatuses(actionplanData, statuses)
        };
    }

    componentDidMount() {
        this._isMounted = true;
        reaction (() => this.props.ticketStore?.allTickets, actionplans => {
            if (!actionplans) { actionplans = [];}
            const data = ActionplanData.setActionplans(this.state.actionplanData, actionplans);
            this.setState({
                actionplanData: data
            });
        });
    }

    toggleNav = (navId: string) => {
        const newStatuses = this.state.statuses;
        // If grand total was set, reset to show all
        if (navId === categories.GRAND_TOTAL) {
            newStatuses[categories.OPEN_LATE] = false;
            newStatuses[categories.CLOSED_NOT_MET] = false;
            newStatuses[categories.OPEN_ON_TIME] = false;
            newStatuses[categories.CLOSED_MET] = false;
            newStatuses[categories.NO_DATE] = false;

        // Otherwise just toggle it.
        } else {
            newStatuses[navId] = !newStatuses[navId];
        }

        this.setState({
            statuses: newStatuses,
            actionplanData: ActionplanData.setStatuses(this.state.actionplanData, newStatuses)
        }, () => {
            this.props.ticketStore?.filterListByCustom(this.state.actionplanData.filteredActionplans);
        });
    };

    isDefaultStatusView = () => {
        return this.state.statuses[categories.GRAND_TOTAL] && !(
            this.state.statuses[categories.OPEN_LATE] ||
            this.state.statuses[categories.CLOSED_NOT_MET] ||
            this.state.statuses[categories.OPEN_ON_TIME] ||
            this.state.statuses[categories.CLOSED_MET] ||
            this.state.statuses[categories.NO_DATE]
        );
    };

    // Handle date change from filter controls.
    handleDateChanges = (newDateRange) => {
        this.setState({
            actionplanData: ActionplanData.setDateRange(this.state.actionplanData, newDateRange)
        });
    };

    handleFilterChanges = (changes: ActionplanData.FilterChanges) => {
        this.setState({
            actionplanData: ActionplanData.setFilters(this.state.actionplanData, changes)
        }, () => {
            this.props.ticketStore?.filterListByCustom(ActionplanData.actionsForTable(this.state.actionplanData));
        });
    };

    handleActionplanFocus = (actionplan: Ticket | undefined) => {
        this.setState({
            actionplanData: ActionplanData.setFocusedActionplan(this.state.actionplanData, actionplan)
        }, () => {
            this.props.ticketStore?.filterListByCustom(ActionplanData.actionsForTable(this.state.actionplanData));
        });
    };

    handleViewByFocus = (category: string, viewBy: string | undefined) => {
        this.setState({
            actionplanData: ActionplanData.setFocusedViewBy(this.state.actionplanData, category, viewBy)
        }, () => {
            this.props.ticketStore?.filterListByCustom(ActionplanData.actionsForTable(this.state.actionplanData));
        });
    };

    handleSortChange = (category: string, sortDirection: number | undefined) => {
        const actionplanData = ActionplanData.setSort(
            this.state.actionplanData,
            sortDirection === undefined ? undefined : category,
            sortDirection
        );
        this.setState({
            actionplanData: actionplanData
        });
    };

    changeView(newView: string) {
        let actionplanData = this.state.actionplanData;
        if (newView !== "Timeline" && this.state.actionplanData.focusedActionplan) {
            actionplanData = ActionplanData.setFocusedActionplan(this.state.actionplanData, undefined);
        }
        if (newView !== "Status") {
            actionplanData = ActionplanData.setFocusedViewBy(this.state.actionplanData, undefined, undefined);
        }
        this.setState({
            view: newView,
            actionplanData: actionplanData
        }, () => {
            this.props.ticketStore?.filterListByCustom(ActionplanData.actionsForTable(this.state.actionplanData));
        });
    }

    renderStatus() {
        return (
            this.state.actionplanData.filteredActionplans.length > 0 ?
                (
                    <div className="d3Graphs row">
                        <OverdueFilter overdue={this.state.actionplanData.includeOpenLate} />
                        <ActionplanGraphLabels
                                         viewBy={this.state.actionplanData.viewBy}
                                         actionplanData={this.state.actionplanData} />
                        <ActionplanGraph actionplanData={this.state.actionplanData}
                                         category={categories.GRAND_TOTAL}
                                         onSortClick={this.handleSortChange}
                                         onFocus={this.handleViewByFocus}
                        />
                        {this.state.actionplanData.includeAllStatus || this.state.actionplanData.includeOpenLate ?
                            <ActionplanGraph actionplanData={this.state.actionplanData}
                                             category={categories.OPEN_LATE}
                                             onSortClick={this.handleSortChange}
                                             onFocus={this.handleViewByFocus} /> : null}
                        {this.state.actionplanData.includeAllStatus || this.state.actionplanData.includeClosedNotMet ?
                            <ActionplanGraph actionplanData={this.state.actionplanData}
                                             category={categories.CLOSED_NOT_MET}
                                             onSortClick={this.handleSortChange}
                                             onFocus={this.handleViewByFocus} /> : null}
                        {this.state.actionplanData.includeAllStatus || this.state.actionplanData.includeOpenOnTime ?
                            <ActionplanGraph actionplanData={this.state.actionplanData}
                                             category={categories.OPEN_ON_TIME}
                                             onSortClick={this.handleSortChange}
                                             onFocus={this.handleViewByFocus} /> : null}
                        {this.state.actionplanData.includeAllStatus || this.state.actionplanData.includeClosedMet ?
                            <ActionplanGraph actionplanData={this.state.actionplanData}
                                             category={categories.CLOSED_MET}
                                             onSortClick={this.handleSortChange}
                                             onFocus={this.handleViewByFocus} /> : null}
                        {this.state.actionplanData.includeAllStatus || this.state.actionplanData.includeNoDate ?
                            <ActionplanGraph actionplanData={this.state.actionplanData}
                                             category={categories.NO_DATE}
                                             onSortClick={this.handleSortChange}
                                             onFocus={this.handleViewByFocus} />: null}
                    </div>
                )
                : (
                    <div className={"d3NoData"}><p>No Data</p></div>
                )
        );
    }

    renderTimeline() {
        return (
            this.state.actionplanData.filteredActionplans.length > 0 &&
            this.state.actionplanData.counts[categories.GRAND_TOTAL] > this.state.actionplanData.counts[categories.NO_DATE] ?
            (
                <div className="d3Timeline row">
                    <ActionplanTimeline
                        actionplanData={this.state.actionplanData}
                        onFocus={this.handleActionplanFocus}
                    />
                </div>
            ) : (
                <div className={"d3NoData"}><p>No Data</p></div>
            )
        );
    }

    render() {
        return (
            <div className="d3Container">

                <div className={"row view-tabs"}>
                    <div className={"col"}></div>
                    <div className={`col-auto view-tab ${this.state.view === "Status" ? "active": ""}`}
                         onClick={this.state.view === "Status" ? undefined : () => {this.changeView("Status");}}
                    >Status</div>
                    <div className={`col-auto view-tab ${this.state.view === "Timeline" ? "active": ""}`}
                         onClick={this.state.view === "Timeline" ? undefined : () => {this.changeView("Timeline");}}
                    >Timeline</div>
                </div>

                <div className="d3NumberNavContainer row">
                    <D3NumberNav
                        active={true /* Always show grand total */}
                        category={categories.GRAND_TOTAL}
                        count={this.state.actionplanData.counts[categories.GRAND_TOTAL]}
                        onClick={this.toggleNav} />
                    <D3NumberNav
                        active={this.state.statuses[categories.OPEN_LATE] && !this.isDefaultStatusView()}
                        category={categories.OPEN_LATE}
                        count={this.state.actionplanData.counts[categories.OPEN_LATE]}
                        onClick={this.toggleNav} />
                    <D3NumberNav
                        active={this.state.statuses[categories.CLOSED_NOT_MET] && !this.isDefaultStatusView()}
                        category={categories.CLOSED_NOT_MET}
                        count={this.state.actionplanData.counts[categories.CLOSED_NOT_MET]}
                        onClick={this.toggleNav} />
                    <D3NumberNav
                        active={this.state.statuses[categories.OPEN_ON_TIME] && !this.isDefaultStatusView()}
                        category={categories.OPEN_ON_TIME}
                        count={this.state.actionplanData.counts[categories.OPEN_ON_TIME]}
                        onClick={this.toggleNav} />
                    <D3NumberNav
                        active={this.state.statuses[categories.CLOSED_MET] && !this.isDefaultStatusView()}
                        category={categories.CLOSED_MET}
                        count={this.state.actionplanData.counts[categories.CLOSED_MET]}
                        onClick={this.toggleNav} />
                    <D3NumberNav
                        active={this.state.statuses[categories.NO_DATE] && !this.isDefaultStatusView()}
                        category={categories.NO_DATE}
                        count={this.state.actionplanData.counts[categories.NO_DATE]}
                        onClick={this.toggleNav} />
                </div>
                <div className="d3Controls row">
                    <div className={"col-auto"}>
                        <ActionplanFilters actionplanData={this.state.actionplanData}
                                           onChange={this.handleFilterChanges }/>
                    </div>
                    <div className="col"></div>
                    {
                        this.state.actionplanData.minDate !== undefined && this.state.actionplanData.maxDate !== undefined ?
                        <DateSlider
                            minDate={this.state.actionplanData.minDate}
                            maxDate={this.state.actionplanData.maxDate}
                            onChange={this.handleDateChanges} /> :
                        null
                    }
                 </div>
                <div className="d3Content row">
                { this.state.view === "Status" ? this.renderStatus() : null}
                { this.state.view === "Timeline" ? this.renderTimeline() : null}
                </div>
            </div>
        );
    }
}

function OverdueFilter ({overdue}: {overdue: boolean}) {
    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        if (overdue) {
            searchParams.set("overdue", "true");
            setSearchParams(searchParams);
        } else {
            searchParams.delete("overdue");
            setSearchParams(searchParams);
        }
    }, [overdue]);
    return <></>;
}
